/**
 * Data Transformation Utilities
 *
 * These utilities transform data between the Supabase database format
 * and the UI component format. This ensures the components stay compatible
 * with the existing mock data structure while using real backend data.
 */

import {
  Customer,
  DatabaseCustomer,
  ApartmentComplex,
  DatabaseComplex,
  User,
  DatabaseUser,
  PropertyManagementCompany,
  DatabaseCompany,
  AnonymousUpload,
  DatabaseAnonymousUpload,
  PaymentStatus,
  ProcessingStatus,
  UserRole,
} from '../types';
import { validateLLMResult } from './llmResultValidation';
import { logger } from '../lib/logger';
import { TIME_MS } from '../constants';

/**
 * Type guard to validate if a value is a valid PaymentStatus enum value
 */
const isValidPaymentStatus = (value: unknown): value is PaymentStatus => {
  return Object.values(PaymentStatus).includes(value as PaymentStatus);
};

/**
 * Type guard to validate if a value is a valid ProcessingStatus enum value
 */
const isValidProcessingStatus = (value: unknown): value is ProcessingStatus => {
  return Object.values(ProcessingStatus).includes(value as ProcessingStatus);
};

/**
 * Transform database anonymous upload to UI format.
 * Validates LLM results at the data boundary to prevent malformed data from causing crashes.
 */
export const transformDatabaseAnonymousUpload = (dbUpload: DatabaseAnonymousUpload): AnonymousUpload => {
  // Validate LLM result at the data boundary
  let validatedLlmResult = null;
  if (dbUpload.llm_result !== null && dbUpload.llm_result !== undefined) {
    validatedLlmResult = validateLLMResult(dbUpload.llm_result, dbUpload.id);

    // Log malformed data for investigation
    if (validatedLlmResult._validationErrors.length > 0) {
      logger.warn('Malformed LLM result detected during transformation', {
        uploadId: dbUpload.id,
        errors: validatedLlmResult._validationErrors,
        wasValidated: validatedLlmResult._wasValidated,
      });
    }
  }

  // Validate enum values with safe defaults
  let paymentStatus = PaymentStatus.PENDING;
  if (isValidPaymentStatus(dbUpload.payment_status)) {
    paymentStatus = dbUpload.payment_status;
  } else if (dbUpload.payment_status !== null && dbUpload.payment_status !== undefined) {
    logger.warn('Invalid payment_status value in database record', {
      uploadId: dbUpload.id,
      invalidValue: dbUpload.payment_status,
      defaultedTo: PaymentStatus.PENDING,
    });
  }

  let processingStatus = ProcessingStatus.PENDING;
  if (isValidProcessingStatus(dbUpload.processing_status)) {
    processingStatus = dbUpload.processing_status;
  } else if (dbUpload.processing_status !== null && dbUpload.processing_status !== undefined) {
    logger.warn('Invalid processing_status value in database record', {
      uploadId: dbUpload.id,
      invalidValue: dbUpload.processing_status,
      defaultedTo: ProcessingStatus.PENDING,
    });
  }

  return {
    id: dbUpload.id,
    name: dbUpload.user_name || 'Anonymous',
    email: dbUpload.user_email || '',
    phone: dbUpload.phone || '',
    uploadDate: dbUpload.created_at || new Date().toISOString(),

    paymentStatus,
    paymentAmount: dbUpload.payment_amount || 0,
    processingStatus,
    pdfUrl: dbUpload.file_path || undefined,
    filePath: dbUpload.file_path || undefined,
    fileName: dbUpload.file_name || undefined,
    fileSize: dbUpload.file_size || undefined,
    mimeType: dbUpload.mime_type || undefined,
    llmResult: validatedLlmResult,
    complexId: dbUpload.complex_id || undefined,
    managerNotes: dbUpload.manager_notes || undefined,
    reviewedBy: dbUpload.reviewed_by || undefined,
    reviewedAt: dbUpload.reviewed_at || undefined,
    createdAt: dbUpload.created_at || new Date().toISOString(),
    updatedAt: dbUpload.updated_at || new Date().toISOString(),
    // Reconciliation fields
    rawPropertyName: dbUpload.raw_property_name || undefined,
    rawPropertyAddress: dbUpload.raw_property_address || undefined,
    placeId: dbUpload.place_id || undefined,
    addressValidationStatus: dbUpload.address_validation_status,
    claimedAt: dbUpload.claimed_at || undefined,
    claimedBy: dbUpload.claimed_by || undefined,
    claimStatus: dbUpload.claim_status as AnonymousUpload['claimStatus'],
    authenticityScore: dbUpload.authenticity_score || undefined,
  };
};

/**
 * Transform an anonymous upload to a UI customer format
 * This bridges the gap between the upload system and the legacy customer-based UI
 */
export const transformUploadToCustomer = (upload: AnonymousUpload): Customer => {
  let status: 'pending' | 'approved' | 'rejected' = 'pending';

  if (upload.processingStatus === 'completed') {
    status = 'approved';
  } else if (upload.processingStatus === 'failed') {
    status = 'rejected';
  }

  return {
    id: upload.id,
    name: upload.name || 'Anonymous',
    status: status,
    dateSubmitted: upload.uploadDate,
    // Convert cents to dollars for display
    amountPaid: (upload.paymentAmount || 0) / 100,
  };
};

/**
 * Transform a list of anonymous uploads to customers
 */
export const transformUploadsToCustomers = (uploads: AnonymousUpload[]): Customer[] => {
  return uploads.map(transformUploadToCustomer);
};

/**
 * Transform a database customer to UI customer format
 */
export const transformDatabaseCustomer = (dbCustomer: DatabaseCustomer): Customer => {
  return {
    id: dbCustomer.id,
    name: dbCustomer.name,
    status: dbCustomer.status,
    dateSubmitted: dbCustomer.date_submitted,
    amountPaid: dbCustomer.amount_paid,
  };
};

/**
 * Transform a list of database customers
 */
export const transformDatabaseCustomers = (
  dbCustomers: DatabaseCustomer[]
): Customer[] => {
  return dbCustomers.map(transformDatabaseCustomer);
};

/**
 * Generate initials from a name
 * Handles edge cases: null/undefined, empty strings, consecutive spaces
 */
export const generateInitials = (name: string | null | undefined): string => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  return name
    .split(' ')
    .filter((part) => part.length > 0)
    .map((part) => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Transform a database user to UI user format
 */
export const transformDatabaseUser = (dbUser: DatabaseUser): User => {
  return {
    id: dbUser.id,
    name: dbUser.name || '',
    email: dbUser.email || '',
    role: (dbUser.role || 'PROPERTY_MANAGER') as UserRole,
    initials: generateInitials(dbUser.name),
    assignedComplexCount: 0, // Derived from user_complex_assignments junction table, not legacy array column
    assignedComplexIds: [], // Populated via separate junction table query when needed
    companyId: dbUser.company_id ?? undefined,
  };
};

/**
 * Transform a list of database users
 */
export const transformDatabaseUsers = (dbUsers: DatabaseUser[]): User[] => {
  return dbUsers.map(transformDatabaseUser);
};

/**
 * Calculate statistics for an apartment complex
 */
interface ComplexStats {
  totalCustomers: number;
  weeklyCustomers: number;
  weeklyRevenue: number;
  trend: number[];
}

export const calculateComplexStats = (
  customers: Customer[],
  daysBack: number = 7
): ComplexStats => {
  const now = new Date();
  const cutoffDate = new Date(now.getTime() - daysBack * TIME_MS.ONE_DAY);

  // Calculate weekly metrics
  const weeklyCustomers = customers.filter((c) => {
    return new Date(c.dateSubmitted) >= cutoffDate;
  }).length;

  const weeklyRevenue = customers
    .filter((c) => new Date(c.dateSubmitted) >= cutoffDate)
    .reduce((sum, c) => sum + c.amountPaid, 0);

  // Calculate trend data for last 7 days
  // Use push() + reverse() instead of unshift() in loop to reduce O(n²) to O(n)
  const trend: number[] = [];
  for (let i = 0; i < 7; i++) {
    const dayStart = new Date(now.getTime() - (i + 1) * TIME_MS.ONE_DAY);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + TIME_MS.ONE_DAY);

    const dayCount = customers.filter((c) => {
      const customerDate = new Date(c.dateSubmitted);
      return customerDate >= dayStart && customerDate < dayEnd;
    }).length;

    trend.push(dayCount);
  }
  trend.reverse();

  return {
    totalCustomers: customers.length,
    weeklyCustomers,
    weeklyRevenue,
    trend,
  };
};

/**
 * Transform a database complex to UI complex format
 * Requires the customers for that complex to calculate stats
 */
export const transformDatabaseComplex = (
  dbComplex: DatabaseComplex,
  customers: Customer[]
): ApartmentComplex => {
  const stats = calculateComplexStats(customers);

  return {
    id: dbComplex.id,
    name: dbComplex.name,
    address: dbComplex.address || '',
    totalCustomers: stats.totalCustomers,
    weeklyCustomers: stats.weeklyCustomers,
    weeklyRevenue: stats.weeklyRevenue,
    recentActivity: customers.slice(0, 3),
    trend: stats.trend,
  };
};

/**
 * Transform a database company to UI company format
 * Requires complexes and users associated with the company
 */
export const transformDatabaseCompany = (
  dbCompany: DatabaseCompany,
  complexes: ApartmentComplex[],
  users: User[]
): PropertyManagementCompany => {
  return {
    id: dbCompany.id,
    name: dbCompany.name,
    phoneNumber: dbCompany.phone_number || '',
    complexes,
    users,
  };
};

/**
 * Transform UI user format back to database format for API requests
 */
export const transformUserToDatabaseFormat = (
  user: User
): Partial<DatabaseUser> => {
  return {
    name: user.name,
    email: user.email,
    role: user.role,
    // assigned_complex_ids removed — junction table user_complex_assignments is the source of truth
    company_id: user.companyId,
  };
};

/**
 * Group customers by date for chart data
 */
export interface ChartDataPoint {
  date: string;
  count: number;
}

export const groupCustomersByDate = (customers: Customer[]): ChartDataPoint[] => {
  const grouped: Record<string, number> = {};

  customers.forEach((customer) => {
    const date = new Date(customer.dateSubmitted).toLocaleDateString();
    grouped[date] = (grouped[date] || 0) + 1;
  });

  return Object.entries(grouped)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (
  current: number,
  previous: number
): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Filter customers by date range
 */
export const filterCustomersByDateRange = (
  customers: Customer[],
  startDate: Date,
  endDate: Date
): Customer[] => {
  return customers.filter((customer) => {
    const customerDate = new Date(customer.dateSubmitted);
    return customerDate >= startDate && customerDate <= endDate;
  });
};

/**
 * Sort customers by date (most recent first)
 */
export const sortCustomersByDate = (customers: Customer[]): Customer[] => {
  return [...customers].sort(
    (a, b) =>
      new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()
  );
};

/**
 * Calculate aggregate statistics for multiple complexes
 */
interface AggregateStats {
  totalRevenue: number;
  totalCustomers: number;
  averageComplexRevenue: number;
  topComplex?: {
    name: string;
    revenue: number;
  };
}

export const calculateAggregateStats = (
  complexes: ApartmentComplex[]
): AggregateStats => {
  const totalRevenue = complexes.reduce((sum, c) => sum + c.weeklyRevenue, 0);
  const totalCustomers = complexes.reduce((sum, c) => sum + c.weeklyCustomers, 0);
  const averageComplexRevenue =
    complexes.length > 0 ? totalRevenue / complexes.length : 0;

  let topComplex: { name: string; revenue: number } | undefined;
  if (complexes.length > 0) {
    const maxRevenue = Math.max(...complexes.map((c) => c.weeklyRevenue));
    const complexWithMax = complexes.find((c) => c.weeklyRevenue === maxRevenue);
    if (complexWithMax) {
      topComplex = {
        name: complexWithMax.name,
        revenue: complexWithMax.weeklyRevenue,
      };
    }
  }

  return {
    totalRevenue,
    totalCustomers,
    averageComplexRevenue,
    topComplex,
  };
};
